'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import type {
  FinderData,
  FinderGroup,
  LocationCongregation,
  LocationGroup,
  MyGroup,
  MyInquiry,
} from '@/lib/types';
import { campusSvgMap } from '@/lib/types';
import {
  cancelInquiry,
  fetchFinderData,
  fetchHomeCampus,
  fetchLocations,
  fetchMyGroups,
  setHomeCampus,
} from '@/lib/api';
import { campusSlug, getSelectedCampusId, onCampusChange, setSelectedCampus } from '@/lib/campus';
import { buildCampusIndex } from '@/lib/campusIndex';
import {
  EMPTY_FILTERS,
  applyRawParams,
  filterGroups,
  syncFiltersToUrl,
  type FilterState,
} from '@/lib/filters';
import { pluralGroups } from '@/lib/format';
import { CampusDropdown } from './CampusDropdown';
import { CampusSvgProvider } from './CampusIcon';
import { FilterBar } from './FilterBar';
import { FilterSheet } from './FilterSheet';
import { GroupCard } from './GroupCard';
import { GroupSheet } from './GroupSheet';
import { JoinSheet, hasPendingJoin, type JoinPrefill } from './JoinSheet';
import { SeeJesusSheet } from './SeeJesus';
import { FilterBarSkeleton, GroupCardSkeleton } from './Skeletons';

export interface GroupFinderProps {
  apiBaseUrl: string;
  /**
   * Filters baked into the embed (`data-params="campus=troy"` on a campus
   * page). Applied over the visitor's default campus, under the page URL.
   */
  embedParams?: [string, string][];
  /** Override the details link base (defaults to the MP widget setting) */
  detailsUrl?: string;
  /** Override the general inquiry form link */
  inquiryFormUrl?: string;
}

type Status = 'loading' | 'ready' | 'error';

function urlEntries(): [string, string][] {
  if (typeof window === 'undefined') return [];
  try {
    return Array.from(new URLSearchParams(window.location.search).entries());
  } catch {
    return [];
  }
}

const HELP_CTA =
  'inline-flex items-center gap-1 rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95';

export function GroupFinder({
  apiBaseUrl,
  embedParams = [],
  detailsUrl,
  inquiryFormUrl,
}: GroupFinderProps) {
  const [data, setData] = useState<FinderData | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [locations, setLocations] = useState<LocationGroup[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [myGroups, setMyGroups] = useState<MyGroup[]>([]);
  const [myInquiries, setMyInquiries] = useState<MyInquiry[]>([]);
  const [homeCampus, setHomeCampusState] = useState<number | null | undefined>(undefined);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<FinderGroup | null>(null);
  const [explainSeeJesus, setExplainSeeJesus] = useState(false);
  // "Help me find a group": MP's Join a Group form in a sheet. Reopens on
  // its own after a sign-in round trip that left a request waiting.
  const [joinOpen, setJoinOpen] = useState(() => hasPendingJoin());
  const rootRef = useRef<HTMLDivElement>(null);
  // Cancelling an inquiry from the sheet: the sheet closes at once, the
  // pinned card shows it is being cancelled until MP answers, then it
  // leaves the pinned section and the group's card in the results below
  // gets a moment's ring so they can find it again.
  const [cancellingGroups, setCancellingGroups] = useState<Set<number>>(() => new Set());
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [highlightGroup, setHighlightGroup] = useState<number | null>(null);
  const scrollTarget = useRef<number | null>(null);
  const revealCancelled = useCallback(() => {
    const id = scrollTarget.current;
    if (id === null) return;
    scrollTarget.current = null;
    // The page has just lost the pinned card, so measure after that settles.
    window.setTimeout(() => {
      const cards = rootRef.current?.querySelectorAll(`[data-group-id="${id}"]`);
      cards?.[cards.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }, []);
  // The help bar slides in a few seconds after load, so it catches the eye
  // once someone has started reading rather than competing with the list.
  const [helpShown, setHelpShown] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setHelpShown(true), 3000);
    return () => window.clearTimeout(t);
  }, []);
  const initialised = useRef(false);

  // Load everything once. The catalogue is required; campuses, the
  // visitor's home campus and their own groups are extras that degrade
  // quietly. Filtering happens in memory from here on.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setStatus('loading');

    fetchFinderData(apiBaseUrl, signal)
      .then((d) => {
        setData(d);
        setStatus('ready');
      })
      .catch((err) => {
        if (signal.aborted) return;
        console.error('Group finder failed to load groups:', err);
        setStatus('error');
      });

    fetchLocations(apiBaseUrl, signal)
      .then((l) => setLocations(l))
      .catch((err) => {
        if (!signal.aborted) console.warn('Group finder: campuses unavailable', err);
      })
      .finally(() => {
        if (!signal.aborted) setLocationsLoading(false);
      });

    fetchHomeCampus(apiBaseUrl, signal).then((id) => {
      if (!signal.aborted) setHomeCampusState(id);
    });

    fetchMyGroups(apiBaseUrl, signal).then((mine) => {
      if (signal.aborted || !mine) return;
      setMyGroups(mine.groups);
      setMyInquiries(mine.inquiries ?? []);
    });

    return () => controller.abort();
  }, [apiBaseUrl]);

  // Slugs in links and embeds ("campus=troy") resolve through MP's real
  // Campus_Slug from /api/locations, with names and the proc's labels as
  // fallbacks, so the index waits for both fetches.
  const campusIndex = useMemo(
    () => buildCampusIndex(locations, data?.options.campuses ?? []),
    [locations, data]
  );

  // Initial filters, once the catalogue, the home-campus lookup and the
  // campus list are all in: signed-in home campus > campus cookie < embed
  // data-params < page URL.
  useEffect(() => {
    if (!data || homeCampus === undefined || locationsLoading || initialised.current) return;
    initialised.current = true;
    const known = (id: number | null) =>
      id !== null && data.options.campuses.some((c) => c.id === id);
    const resolve = (v: string) => campusIndex.resolve(v);

    let next: FilterState = { ...EMPTY_FILTERS };
    const cookieCampus = getSelectedCampusId();
    if (known(homeCampus)) next = { ...next, campus: homeCampus };
    else if (known(cookieCampus)) next = { ...next, campus: cookieCampus };
    next = applyRawParams(next, embedParams, resolve);
    next = applyRawParams(next, urlEntries(), resolve);
    // Pinned groups (an email's ?groups=, MP's finder preview) are the point of
    // the link: a remembered campus must not hide them. Only a campus the link
    // itself names still applies.
    if (next.groupIds.length > 0) {
      const asked = applyRawParams(
        applyRawParams({ ...EMPTY_FILTERS }, embedParams, resolve),
        urlEntries(),
        resolve
      );
      if (asked.campus === null) next = { ...next, campus: null };
    }
    setFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, homeCampus, locationsLoading]);

  // Follow the site-wide campus picker without a reload.
  useEffect(() => {
    return onCampusChange((id) => {
      setFilters((f) => (f.campus === id ? f : { ...f, campus: id }));
    });
  }, []);

  // Keep the URL shareable.
  useEffect(() => {
    if (!initialised.current) return;
    syncFiltersToUrl(filters, campusIndex.slugFor);
  }, [filters, campusIndex]);

  const svgMap = useMemo(() => campusSvgMap(locations), [locations]);
  const myGroupById = useMemo(() => new Map(myGroups.map((g) => [g.id, g])), [myGroups]);
  const inquiriesByGroup = useMemo(() => {
    const m = new Map<number, MyInquiry[]>();
    for (const i of myInquiries) m.set(i.groupId, [...(m.get(i.groupId) ?? []), i]);
    return m;
  }, [myInquiries]);

  // After an inquiry goes out, the pinned section and badges catch up.
  const refreshMine = useCallback(() => {
    void fetchMyGroups(apiBaseUrl).then((mine) => {
      if (!mine) return;
      setMyGroups(mine.groups);
      setMyInquiries(mine.inquiries ?? []);
    });
  }, [apiBaseUrl]);

  const handleCancel = useCallback(
    async (inq: MyInquiry) => {
      setSelected(null);
      setCancelError(null);
      setCancellingGroups((cur) => new Set(cur).add(inq.groupId));
      try {
        await cancelInquiry(apiBaseUrl, inq.groupId, inq.id);
        setMyInquiries((cur) => cur.filter((i) => i.id !== inq.id));
        setHighlightGroup(inq.groupId);
        window.setTimeout(() => setHighlightGroup((h) => (h === inq.groupId ? null : h)), 3000);
        // Once the pinned card has finished leaving, `onExitComplete` below
        // brings the group's card in the results into view.
        scrollTarget.current = inq.groupId;
        refreshMine();
      } catch (err) {
        setCancelError(err instanceof Error ? err.message : 'Could not cancel the inquiry');
      } finally {
        setCancellingGroups((cur) => {
          const next = new Set(cur);
          next.delete(inq.groupId);
          return next;
        });
      }
    },
    [apiBaseUrl, refreshMine]
  );

  // The visitor's own groups, and the ones their household has inquired
  // into, sit above the results and are never filtered out. Closed
  // inquiries stay out of the pinned section unless asked for; their cards
  // still carry the "Inquiry closed" badge wherever they appear.
  const [showClosed, setShowClosed] = useState(false);
  const hasOpenInquiry = useCallback(
    (id: number) => (inquiriesByGroup.get(id) ?? []).some((i) => i.status !== 'closed'),
    [inquiriesByGroup]
  );
  const pinned = useCallback(
    (g: FinderGroup) =>
      myGroupById.has(g.id) || hasOpenInquiry(g.id) || (showClosed && inquiriesByGroup.has(g.id)),
    [myGroupById, inquiriesByGroup, hasOpenInquiry, showClosed]
  );
  const mineInCatalogue = useMemo(() => (data ? data.groups.filter(pinned) : []), [data, pinned]);
  const onlyInquiries = mineInCatalogue.every((g) => !myGroupById.has(g.id));
  const closedCount = useMemo(
    () =>
      data
        ? data.groups.filter(
            (g) => !myGroupById.has(g.id) && inquiriesByGroup.has(g.id) && !hasOpenInquiry(g.id)
          ).length
        : 0,
    [data, myGroupById, inquiriesByGroup, hasOpenInquiry]
  );

  // The filters they set are the answers they would give: hand them over.
  const joinPrefill = useMemo<JoinPrefill>(() => {
    const label = (list: { id: number; label: string }[] | undefined, id: number | null) =>
      id === null ? undefined : list?.find((o) => o.id === id)?.label;
    const answers: JoinPrefill['answers'] = {};
    const kids =
      filters.kids === 1 ? 'Kids Welcome' : filters.kids === 0 ? 'Adults Only' : undefined;
    if (kids) answers['Family Accommodation'] = kids;
    const household = label(data?.options.households, filters.household);
    if (household) answers['Household Type'] = household;
    const gender = label(data?.options.genders, filters.gender);
    if (gender) answers['Gender Specific Groups'] = gender;
    const lifeStage = label(data?.options.lifeStages, filters.lifeStage);
    if (lifeStage) answers['Life Stage'] = lifeStage;
    if (filters.days.length) answers['Meeting Days'] = filters.days;
    return { campusId: filters.campus ?? homeCampus ?? null, answers };
  }, [filters, data, homeCampus]);

  const result = useMemo(() => {
    if (!data) return { open: [], unavailable: [] };
    const rest = data.groups.filter((g) => !pinned(g));
    return filterGroups(rest, filters);
  }, [data, filters, pinned]);

  const links = {
    details:
      detailsUrl || data?.links.details || 'https://woodsidebible.org/group-finder/details/?id=',
    inquiryForm:
      inquiryFormUrl || data?.links.inquiryForm || 'https://woodsidebible.org/group-finder/join/',
  };

  const toggleShowFull = () => setFilters((f) => ({ ...f, showFull: !f.showFull }));

  // Picking a campus in the dropdown behaves like the site header's picker:
  // cookie + event for every other widget on the page, and the MP home
  // campus when signed in. "All campuses" only widens this list.
  const handleCampusSelect = useCallback(
    (cong: LocationCongregation | null, svgUrl: string | null) => {
      setFilters((f) => ({ ...f, campus: cong ? cong.id : null }));
      if (!cong) return;
      setSelectedCampus({
        congregationId: cong.id,
        name: cong.name,
        slug: cong.campus_slug ?? campusSlug(cong.name),
        svgUrl,
      });
      void setHomeCampus(apiBaseUrl, cong.id);
    },
    [apiBaseUrl]
  );

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-gray-200 bg-white px-6 py-14 text-center">
        <p className="text-lg font-bold text-[#1c2b39]">We couldn't load groups right now.</p>
        <p className="max-w-md text-sm text-gray-600">
          Please try again in a moment. If you'd rather talk to a person, we're happy to help you
          find a group.
        </p>
        <button
          type="button"
          onClick={() => setJoinOpen(true)}
          className={`${HELP_CTA} bg-[#299c8f]`}
        >
          Help me find a group
          <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </div>
    );
  }

  const ready = status === 'ready' && data !== null;

  return (
    <CampusSvgProvider value={svgMap}>
      <div ref={rootRef} className="flex flex-col gap-5 pb-24 text-[#1c2b39]">
        {/* The visitor's own groups sit above the filters and ignore them: you
            always see every group you are in. */}
        {ready && (mineInCatalogue.length > 0 || closedCount > 0) && (
          <section className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-xs font-bold tracking-widest text-gray-500 uppercase">
                {onlyInquiries
                  ? mineInCatalogue.length === 1
                    ? 'Your inquiry'
                    : 'Your inquiries'
                  : mineInCatalogue.length === 1
                    ? 'Your group'
                    : 'Your groups'}
              </h3>
              {closedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowClosed((v) => !v)}
                  aria-pressed={showClosed}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-all hover:scale-105 hover:shadow-md active:scale-95 ${
                    showClosed
                      ? 'bg-[#1c2b39] text-white shadow-sm'
                      : 'bg-white text-gray-600 ring-1 ring-gray-200'
                  }`}
                >
                  {showClosed
                    ? 'Hide closed'
                    : `Show ${closedCount} closed ${closedCount === 1 ? 'inquiry' : 'inquiries'}`}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence initial={false} onExitComplete={revealCancelled}>
                {mineInCatalogue.map((g) => (
                  <motion.div
                    key={g.id}
                    layout
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.25 }}
                  >
                    <GroupCard
                      group={g}
                      mine={myGroupById.get(g.id)}
                      inquiries={inquiriesByGroup.get(g.id)}
                      busy={cancellingGroups.has(g.id) ? 'Cancelling…' : undefined}
                      detailsUrl={links.details}
                      onOpen={setSelected}
                      onExplainSeeJesus={() => setExplainSeeJesus(true)}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
            {cancelError && <p className="text-sm font-semibold text-red-600">{cancelError}</p>}
          </section>
        )}

        {!ready ? (
          <FilterBarSkeleton />
        ) : (
          <FilterBar
            options={data.options}
            filters={filters}
            onChange={setFilters}
            onOpenFilters={() => setFiltersOpen(true)}
            campusControl={
              <CampusDropdown
                locations={locations}
                selectedId={filters.campus}
                onSelect={handleCampusSelect}
                loading={locationsLoading}
              />
            }
          />
        )}

        {!ready ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
            <GroupCardSkeleton />
            <GroupCardSkeleton />
            <GroupCardSkeleton />
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500" aria-live="polite">
              {result.open.length === 0
                ? 'No groups with room match your filters'
                : `${pluralGroups(result.open.length)} with room`}
              {result.unavailable.length > 0 && (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={toggleShowFull}
                    className="font-semibold text-[#1c2b39] underline-offset-2 hover:underline"
                  >
                    {filters.showFull
                      ? `hide the ${result.unavailable.length} full`
                      : `${result.unavailable.length} full`}
                  </button>
                </>
              )}
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {result.open.map((g) => (
                <GroupCard
                  key={g.id}
                  group={g}
                  inquiries={inquiriesByGroup.get(g.id)}
                  highlight={highlightGroup === g.id}
                  detailsUrl={links.details}
                  onOpen={setSelected}
                  onExplainSeeJesus={() => setExplainSeeJesus(true)}
                />
              ))}

              {result.open.length === 0 && (
                <div className="col-span-full flex flex-col items-center gap-4 rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
                  <p className="text-lg font-bold text-[#1c2b39]">No groups found</p>
                  <p className="max-w-md text-sm text-gray-600">
                    {result.unavailable.length > 0
                      ? `${pluralGroups(result.unavailable.length)} match but ${
                          result.unavailable.length === 1 ? 'is' : 'are'
                        } full or on a break right now; they're listed below. Try widening your filters, or let us help you find one.`
                      : 'Try widening your filters, or let us help you find one.'}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => setFilters({ ...EMPTY_FILTERS })}
                      className="rounded-full bg-gray-100 px-5 py-2.5 text-sm font-bold text-[#1c2b39] transition-all hover:scale-105 active:scale-95"
                    >
                      Clear filters
                    </button>
                  </div>
                </div>
              )}

              {result.unavailable.length > 0 && (
                <div className="col-span-full mt-2 flex items-center gap-3">
                  <span className="h-px flex-1 bg-gray-200" />
                  <button
                    type="button"
                    onClick={toggleShowFull}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-bold tracking-widest text-gray-500 uppercase ring-1 ring-gray-200 transition-colors ring-inset hover:text-[#1c2b39]"
                  >
                    {filters.showFull ? (
                      <>
                        Full or on a break ({result.unavailable.length}) · hide
                        <ChevronUp className="h-3.5 w-3.5" />
                      </>
                    ) : (
                      <>
                        Show {pluralGroups(result.unavailable.length)} that{' '}
                        {result.unavailable.length === 1 ? 'is' : 'are'} full or on a break
                        <ChevronDown className="h-3.5 w-3.5" />
                      </>
                    )}
                  </button>
                  <span className="h-px flex-1 bg-gray-200" />
                </div>
              )}

              {filters.showFull &&
                result.unavailable.map((g) => (
                  <GroupCard
                    key={g.id}
                    group={g}
                    inquiries={inquiriesByGroup.get(g.id)}
                    highlight={highlightGroup === g.id}
                    detailsUrl={links.details}
                    onOpen={setSelected}
                    onExplainSeeJesus={() => setExplainSeeJesus(true)}
                  />
                ))}
            </div>

            {/* Floats over the page bottom so the offer is always one tap away
                while someone scrolls the list. Fixed inside the shadow root is
                fine here: the theme's stacking contexts only bite when we need
                to sit above the site header, and this lives at the bottom. */}
            <div
              className="pointer-events-none fixed inset-x-0 bottom-0 z-10 flex justify-center px-4"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
            >
              {/* Phones get the bare button; wider screens get the glass pill with the question. */}
              <div
                className={`pointer-events-auto flex max-w-full items-center gap-3 transition-all duration-500 ease-out motion-reduce:transition-none sm:rounded-full sm:border sm:border-white/60 sm:bg-white/75 sm:p-1.5 sm:pl-5 sm:shadow-[0_8px_30px_rgba(28,43,57,0.18)] sm:backdrop-blur-xl sm:backdrop-saturate-150 ${
                  helpShown ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
              >
                <p className="hidden text-sm text-gray-700 sm:block">
                  Not sure which group is right for you?
                </p>
                <button
                  type="button"
                  onClick={() => setJoinOpen(true)}
                  className="inline-flex items-center gap-1 rounded-full bg-[#299c8f] px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(41,156,143,0.4)] transition-all hover:scale-105 active:scale-95 sm:shadow-none"
                >
                  Help me find a group
                  <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </>
        )}

        {data && (
          <FilterSheet
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            options={data.options}
            filters={filters}
            onChange={setFilters}
            openCount={result.open.length}
            unavailableCount={result.unavailable.length}
          />
        )}

        <SeeJesusSheet open={explainSeeJesus} onClose={() => setExplainSeeJesus(false)} />
        <GroupSheet
          apiBaseUrl={apiBaseUrl}
          finderUrl={typeof window !== 'undefined' ? window.location.pathname : '/group-finder/'}
          group={selected}
          mine={selected ? myGroupById.get(selected.id) : undefined}
          inquiries={selected ? inquiriesByGroup.get(selected.id) : undefined}
          onInquired={refreshMine}
          onHelp={() => setJoinOpen(true)}
          onCancel={handleCancel}
          detailsUrl={links.details}
          inquiryFormUrl={links.inquiryForm}
          onClose={() => setSelected(null)}
        />
        <JoinSheet
          open={joinOpen}
          onClose={() => setJoinOpen(false)}
          apiBaseUrl={apiBaseUrl}
          locations={locations}
          prefill={joinPrefill}
        />
      </div>
    </CampusSvgProvider>
  );
}
